import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Decode JWT payload without verification (client-side only for expiry check)
const getTokenExpiry = (token) => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.exp ?? null;
  } catch {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Validate token on init: JWT must have 3 segments (header.payload.signature)
  const [token, setToken] = useState(() => {
    const stored = localStorage.getItem('token');
    if (stored && stored.split('.').length === 3) return stored;
    if (stored) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
    return null;
  });
  const retryTimeoutRef = useRef(null);
  // Refs for token refresh logic (avoid stale closures in interceptor)
  const tokenRef = useRef(token);
  const refreshPromiseRef = useRef(null);

  // Keep tokenRef in sync with token state
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  // Auto-refresh: request interceptor checks token expiry before each API call
  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(async (config) => {
      const url = config.url || '';
      // Skip auth endpoints to avoid loops
      if (
        url.includes('/auth/login') ||
        url.includes('/auth/refresh') ||
        url.includes('/auth/register')
      ) {
        return config;
      }

      // Audit-Header: Wenn im Impersonation-Modus, Admin-ID mitsenden
      try {
        const impData = localStorage.getItem('impersonation');
        if (impData) {
          const parsed = JSON.parse(impData);
          const adminUser = parsed.original_user;
          const adminId = typeof adminUser === 'string'
            ? JSON.parse(adminUser)?.id
            : adminUser?.id;
          if (adminId) {
            config.headers = config.headers || {};
            config.headers['X-Impersonated-By'] = adminId;
          }
        }
      } catch { /* Sicher ignorieren */ }

      const currentToken = tokenRef.current;
      if (!currentToken) return config;

      const exp = getTokenExpiry(currentToken);
      if (exp === null) return config;

      const expiresInSeconds = exp - Date.now() / 1000;

      // Helper: attempt token refresh (shared logic for expired + near-expiry)
      const attemptRefresh = async () => {
        if (!refreshPromiseRef.current) {
          refreshPromiseRef.current = axios
            .post(`${API}/auth/refresh`, {}, {
              headers: { Authorization: `Bearer ${currentToken}` }
            })
            .then((res) => {
              const newToken = res.data.access_token;
              tokenRef.current = newToken;
              localStorage.setItem('token', newToken);
              axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
              setToken(newToken);
            })
            .catch(() => {
              // Refresh failed — 401 interceptor will handle logout if needed
            })
            .finally(() => {
              refreshPromiseRef.current = null;
            });
        }
        await refreshPromiseRef.current;
        config.headers = config.headers || {};
        config.headers['Authorization'] = `Bearer ${tokenRef.current}`;
      };

      // Token already expired → try refresh BEFORE sending request
      // (avoids unnecessary 401 when a valid refresh is possible)
      if (expiresInSeconds <= 0) {
        await attemptRefresh();
        return config;
      }

      // Token expiring within 1 hour → proactive refresh (background)
      if (expiresInSeconds < 3600) {
        await attemptRefresh();
      }

      return config;
    });

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
    };
  }, []); // Only once on mount

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
    return () => {
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
    };
  }, [token]);

  // Setup axios interceptor for global 401 handling
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        // Only logout on explicit 401 — NOT on network errors or 5xx during restarts
        if (error.response?.status === 401 && token) {
          console.warn('401 Unauthorized - Logging out');
          logout();
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [token]);

  /**
   * Fetch user with retry logic.
   * During deployment restarts (backend briefly unavailable),
   * we use cached user data and retry instead of logging out.
   */
  const fetchUser = async (retryCount = 0) => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      localStorage.setItem('user', JSON.stringify(response.data));
      setLoading(false);
    } catch (error) {
      const status = error.response?.status;

      if (status === 401) {
        // Token explicitly invalid — must logout
        logout();
        setLoading(false);
        return;
      }

      // Network error or 5xx (e.g. backend restarting during deployment)
      if (retryCount < 3) {
        // Recover from localStorage cache while waiting for backend
        const cachedUserRaw = localStorage.getItem('user');
        if (cachedUserRaw) {
          try {
            setUser(JSON.parse(cachedUserRaw));
            setLoading(false); // Show app with cached user immediately
          } catch (_) {}
        }

        // Retry with exponential backoff: 2s, 4s, 8s
        const delay = 2000 * Math.pow(2, retryCount);
        console.warn(`Auth fetch failed (${status || 'network'}), retrying in ${delay}ms... (attempt ${retryCount + 1}/3)`);
        retryTimeoutRef.current = setTimeout(() => fetchUser(retryCount + 1), delay);
      } else {
        // After 3 retries without 401 — keep cached user or set null
        const cachedUserRaw = localStorage.getItem('user');
        if (cachedUserRaw) {
          try {
            setUser(JSON.parse(cachedUserRaw));
          } catch (_) {
            setUser(null);
          }
        }
        setLoading(false);
      }
    }
  };

  const login = async (email, password, rememberMe = false, totpCode = null) => {
    const payload = {
      email,
      password,
      remember_me: rememberMe
    };
    
    if (totpCode) {
      payload.totp_code = totpCode;
    }
    
    const response = await axios.post(`${API}/auth/login`, payload);
    const { access_token, user: userData } = response.data;
    setToken(access_token);
    setUser(userData);
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    
    window.dispatchEvent(new Event('userLoggedIn'));
    
    return userData;
  };

  const register = async (data) => {
    const response = await axios.post(`${API}/auth/register`, data);
    
    if (response.data.access_token && response.data.user) {
      const { access_token, user: userData } = response.data;
      setToken(access_token);
      setUser(userData);
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      window.dispatchEvent(new Event('userLoggedIn'));
    }
    
    return response.data;
  };

  const logout = () => {
    if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('impersonation');
    localStorage.removeItem('impersonated_user');
    delete axios.defaults.headers.common['Authorization'];
  };

  // Allow external code to directly restore auth state (e.g. after impersonation exit)
  const restoreAuth = useCallback((restoredToken, restoredUser) => {
    if (!restoredToken) return;
    localStorage.setItem('token', restoredToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${restoredToken}`;
    setToken(restoredToken);
    if (restoredUser) {
      const userObj = typeof restoredUser === 'string' ? JSON.parse(restoredUser) : restoredUser;
      setUser(userObj);
      localStorage.setItem('user', JSON.stringify(userObj));
    }
  }, []);

  const refreshUser = async () => {
    if (token) {
      await fetchUser();
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser, restoreAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
